import Dexie, { type Table } from 'dexie';

export const DATABASE_NAME = 'smartkeur';

export interface MetaRecord {
    key: string;
    value: unknown;
    updatedAt: number;
}

/**
 * De apparaatbrede database: alleen wat bij het apparaat en de persoon hoort,
 * niet bij een keuringsbedrijf - de sessie, het apparaat-id, voorkeuren en de
 * querycache.
 *
 * Dossiers, antwoorden, foto's, de outbox en de templatepakketten staan per
 * omgeving in een eigen database; zie `tenant-db.ts`.
 *
 * Versie 1 had die tabellen hier nog, met de demo-opname erin. Ze konden nooit
 * met een server worden gesynchroniseerd (er was er geen) en verdwijnen in
 * versie 2.
 */
export class SmartkeurDatabase extends Dexie {
    meta!: Table<MetaRecord, string>;

    constructor(name: string = DATABASE_NAME) {
        super(name);

        this.version(1).stores({
            templates: '[id+version], id, slug, publishedAt, cachedAt',
            inspections: 'id, tenantId, status, assigneeId, scheduledAt, updatedAt, syncState',
            answers:
                'id, [inspectionId+questionId], inspectionId, questionId, updatedAt, syncState',
            photos: 'id, inspectionId, [inspectionId+answerId], uploadState, takenAt',
            outbox: '++seq, &id, status, entity, entityId, [status+nextAttemptAt]',
            conflicts: 'id, entity, entityId, detectedAt, resolution',
            meta: 'key',
        });

        this.version(2).stores({
            templates: null,
            inspections: null,
            answers: null,
            photos: null,
            outbox: null,
            conflicts: null,
            meta: 'key',
        });
    }
}

let instance: SmartkeurDatabase | null = null;

/** De apparaatbrede database. Voor dossiers: `getTenantDb()`. */
export function getDb(): SmartkeurDatabase {
    instance ??= new SmartkeurDatabase();

    return instance;
}

/** Alleen voor tests: een geïsoleerde database met een eigen naam. */
export function createTestDb(name = `smartkeur-test-${Math.random().toString(36).slice(2)}`) {
    return new SmartkeurDatabase(name);
}

export async function readMeta<T>(db: SmartkeurDatabase, key: string): Promise<T | undefined> {
    const row = await db.meta.get(key);

    return row?.value as T | undefined;
}

export async function writeMeta(db: SmartkeurDatabase, key: string, value: unknown): Promise<void> {
    await db.meta.put({ key, value, updatedAt: Date.now() });
}

/** Sleutels in de `meta`-tabel. Gebruik deze constanten, geen losse strings. */
export const META_KEYS = {
    deviceId: 'device-id',
    session: 'auth-session',
    queryCache: 'query-cache',
} as const;
